import React, { useState, useRef, useEffect, ReactElement } from 'react';
import { Grid, Box } from '@material-ui/core';
import { Clear, Check } from '@material-ui/icons';
import { PasslyButton } from '@app/components';
import { AutoComplete } from '@itglue/kaseya-ui';
import { TranslationsTypes } from '@app/models';
import { useTypedTranslation } from '@app/hooks';

interface FieldProps {
    inlineedit?: boolean;
    onClickSave?: Function;
    onClickDiscard?: Function;
    focusfunc?: Function;
    buttonWidth?: number;
    buttonFontSize?: 'large' | 'medium' | 'inherit' | 'small';
    // if there is some error with the input, prevent save changes
    editdisabled?: boolean;
    items: any;
    onChange: (e: any) => void;
    className?: string;
    id?: string;
    name?: string;
    label?: TranslationsTypes | string;
    field?: string;
    value?: any;
    dropdown?: boolean;
    valueField?: string;
    appendTo?: Element;
    itemTemplate?: (item: any) => ReactElement;
    multiple?: boolean;
    disabled?: boolean;
    required?: boolean;
}

export function PasslyAutoComplete(props: FieldProps) {
    const { value, items, className, id, name, onChange, label, field = 'name', dropdown = true, editdisabled, valueField = 'code', itemTemplate, multiple, appendTo, disabled } = props;
    const [lastSelectedItem, setLastSelectedItem] = useState<any>(null);
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [filteredItems, setFilteredItems] = useState<any>(null);
    const [isSomethingChanged, setIsSomethingChanged] = useState(false);
    const { translation } = useTypedTranslation();
    const inputRef = useRef<any>(null);

    useEffect(() => {
        if (value && items && typeof value === 'string') {
            const foundedItem = items.find((item: any) => item[valueField] === value);

            if ((selectedItem && selectedItem[valueField] !== value) || !selectedItem) {
                setSelectedItem(foundedItem);
                setLastSelectedItem(foundedItem);
            }
        } else if (value && items && typeof value === 'object') {
            setSelectedItem(value);
            setLastSelectedItem(value);
        }
    }, [items, value]);

    useEffect(() => {
        if ((lastSelectedItem || lastSelectedItem === null) && selectedItem && typeof selectedItem !== 'string') {
            setIsSomethingChanged(lastSelectedItem?.[valueField] !== selectedItem[valueField]);
        }
    }, [lastSelectedItem, selectedItem]);

    const doSearch = (event: any) => {
        const allItems = [...items];
        let filtered = allItems;

        if (event.query.trim().length) {
            filtered = allItems.filter((item: any) => {
                return item[field]?.toLowerCase().startsWith(event.query.toLowerCase());
            });
        }

        setFilteredItems(filtered);
    };

    // from kaseya-ui
    const searchItem = (event: any) => setTimeout(() => doSearch(event), 250);

    const onFormChange = (e: any) => {
        const { value } = e.target;

        if (typeof value !== 'string') {
            onChange(e);
        }

        setSelectedItem(value);
    };

    const handleKeyPress = (e: any) => {
        // esc
        if (e.keyCode === 27 && props.inlineedit) {
            handleDiscard(e, props.name);
        }

        // enter
        if (e.keyCode === 13 && props.inlineedit && !editdisabled) {
            handleSave(e, props.name);
        }
    };

    const handleSave = (e: any, name: string = '') => {
        if (props.inlineedit) {
            props.onClickSave!(e, name);
            setIsSomethingChanged(false);
            setSelectedItem(selectedItem);
        }
    };

    const handleDiscard = (e: any, name: string = '') => {
        if (props.inlineedit) {
            props.onClickDiscard!(e, name);
            setIsSomethingChanged(false);
            setSelectedItem(lastSelectedItem);
        }
    };

    return (
        <>
            <Grid container justifyContent='flex-start' alignItems={'flex-start'} wrap='nowrap' className='autoComplete__wrapper'>
                <Grid item xs={12}>
                    <Box>
                        <AutoComplete
                            {...props}
                            className={className}
                            value={selectedItem}
                            id={id}
                            name={name}
                            suggestions={filteredItems}
                            completeMethod={searchItem}
                            onChange={onFormChange}
                            onKeyPress={handleKeyPress}
                            field={field}
                            label={translation((label ?? '') as TranslationsTypes)}
                            ref={inputRef}
                            dropdown={dropdown}
                            itemTemplate={itemTemplate}
                            multiple={multiple}
                            appendTo={appendTo}
                            disabled={disabled}
                        />
                        {props.inlineedit && isSomethingChanged && (
                            <div className={`autoComplete__buttons-block ${label ? 'autoComplete__buttons-block-with-label' : ''}`}>
                                <Box className='autoComplete__button' ml={0.5} mb={1} width={props.buttonWidth} height={props.buttonWidth}>
                                    <PasslyButton
                                        style={{ width: props.buttonWidth, height: props.buttonWidth, padding: 0, minWidth: props.buttonWidth }}
                                        name={props.name}
                                        variant='outlined'
                                        onClick={(e: any) => handleDiscard(e, props.name)}
                                    >
                                        <Clear fontSize={props.buttonFontSize} name={props.name} color='inherit' />
                                    </PasslyButton>
                                </Box>
                                <Box className='autoComplete__button' ml={0.5} mb={1} width={props.buttonWidth} height={props.buttonWidth}>
                                    <PasslyButton
                                        style={{ width: props.buttonWidth, height: props.buttonWidth, padding: 0, minWidth: props.buttonWidth }}
                                        disabled={editdisabled}
                                        name={props.name}
                                        variant='contained'
                                        color='primary'
                                        onClick={(e: any) => handleSave(e, props.name)}
                                    >
                                        <Check fontSize={props.buttonFontSize} name={props.name} color='inherit' style={{ marginBottom: 2 }} />
                                    </PasslyButton>
                                </Box>
                            </div>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </>
    );
}
